import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon, MagnifyingGlassIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const ScrollableDropdown = ({
    value,
    onChange,
    options = [],
    placeholder = 'Select...',
    buttonHeightClassName = 'h-10',
    maxMenuHeightPx = 300,
    maxMenuHeightClassName = 'max-h-60',
    searchable = false,
    searchPlaceholder = 'Search...',
    compact = false,
    disabled = false,
    className = '',
    open,
    onOpenChange,
    buttonClassName = '',
    menuClassName = '',
    usePortal = true, // Default to true for better positioning
    autoFlip = true,
    isMulti = false, // Support multiple selections
}) => {
    const [isOpen, setIsOpen] = useState(() => !!open);
    const [searchTerm, setSearchTerm] = useState('');
    const [hoveredOption, setHoveredOption] = useState(null);
    const [hoveredRect, setHoveredRect] = useState(null);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const searchInputRef = useRef(null);
    const [menuStyle, setMenuStyle] = useState({ top: 0, left: 0, width: 0 });
    const [isPositioned, setIsPositioned] = useState(false);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
            const inMenu = menuRef.current && menuRef.current.contains(event.target);
            if (!inTrigger && !inMenu) {
                setIsOpen(false);
                setIsPositioned(false);
                setSearchTerm('');
                onOpenChange && onOpenChange(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onOpenChange]);

    // Controlled open state support
    useEffect(() => {
        if (typeof open !== 'undefined') {
            setIsOpen(!!open);
        }
    }, [open]);

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen && searchable && searchInputRef.current) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    }, [isOpen, searchable]);

    const computeMenuStyle = () => {
        if (!dropdownRef.current) return;
        const rect = dropdownRef.current.getBoundingClientRect();

        // Use a fixed max height for computation
        const menuHeight = Math.min(maxMenuHeightPx, (options.length * 40) + (searchable ? 50 : 0) + 8);

        const belowTop = rect.bottom + 4;
        const aboveTop = rect.top - menuHeight - 4;

        let top = belowTop;
        if (autoFlip) {
            const wouldOverflow = belowTop + menuHeight > window.innerHeight;
            if (wouldOverflow && aboveTop >= 0) {
                top = aboveTop;
            }
        }

        setMenuStyle({
            top,
            left: rect.left,
            minWidth: Math.max(rect.width, 160),
        });
        setIsPositioned(true);
    };

    // Compute portal menu position synchronously before browser paint to prevent top-left flickering
    useLayoutEffect(() => {
        if (isOpen && usePortal) {
            computeMenuStyle();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, usePortal, searchable, maxMenuHeightPx, autoFlip, options.length]);

    // Close on scroll or resize when using portal to prevent drifting
    useEffect(() => {
        if (!isOpen || !usePortal) return;

        const handleScroll = (e) => {
            // Do NOT close if the scroll happened inside the dropdown menu itself
            if (e.target && typeof e.target.closest === 'function' && e.target.closest('.portal-dropdown-content')) {
                return;
            }
            setIsOpen(false);
            setIsPositioned(false);
            setSearchTerm('');
            onOpenChange && onOpenChange(false);
        };

        const handleResize = () => {
            setIsOpen(false);
            setIsPositioned(false);
            setSearchTerm('');
            onOpenChange && onOpenChange(false);
        };

        window.addEventListener('scroll', handleScroll, { capture: true, passive: true });
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('scroll', handleScroll, { capture: true });
            window.removeEventListener('resize', handleResize);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, usePortal]);

    const filteredOptions = searchable && searchTerm
        ? options.filter(option =>
            option.label?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : options;

    const isSelected = (optionValue) => {
        if (isMulti) {
            return Array.isArray(value) && value.includes(optionValue);
        }
        return value === optionValue;
    };

    const getDisplayText = () => {
        if (isMulti) {
            if (!Array.isArray(value) || value.length === 0 || (value.length === 1 && value[0] === 'All')) return placeholder;
            if (value.length === 1) return options.find(o => o.value === value[0])?.label || value[0];
            return `${value.length} selected`;
        }
        const selectedOption = options.find(opt => opt.value === value);
        return selectedOption?.label || placeholder;
    };

    const handleSelect = (optionValue) => {
        if (isMulti) {
            let newValue;
            const currentValues = Array.isArray(value) ? value : (value ? [value] : []);
            
            if (optionValue === 'All') {
                newValue = ['All'];
            } else {
                // If 'All' was selected and we select something else, remove 'All'
                const withoutAll = currentValues.filter(v => v !== 'All');
                if (isSelected(optionValue)) {
                    newValue = withoutAll.filter(v => v !== optionValue);
                    if (newValue.length === 0) newValue = ['All'];
                } else {
                    newValue = [...withoutAll, optionValue];
                }
            }
            onChange(newValue);
            // Don't close for multi-select
        } else {
            onChange(optionValue);
            setIsOpen(false);
            setSearchTerm('');
            onOpenChange && onOpenChange(false);
        }
    };

    return (
        <div ref={dropdownRef} className={`relative ${className}`}>
            {/* Dropdown Button */}
            <button
                type="button"
                onClick={() => {
                    if (disabled) return;
                    const next = !isOpen;
                    setIsOpen(next);
                    onOpenChange && onOpenChange(next);
                }}
                disabled={disabled}
                className={`
          w-full ${buttonHeightClassName}
          flex items-center ${!buttonClassName.includes('justify-') ? 'justify-between' : ''}
          focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
          disabled:cursor-not-allowed
          transition-all duration-200
          ${!buttonClassName.match(/text-(xs|sm|base|lg|xl|2xl|\[)/) ? (compact ? 'text-xs' : 'text-sm') : ''}
          ${!buttonClassName.includes('px-') ? 'px-3' : ''}
          ${!buttonClassName.includes('py-') ? (buttonHeightClassName === 'h-6' ? 'py-0.5' : 'py-2') : ''}
          ${!buttonClassName.includes('text-center') && !buttonClassName.includes('text-right') ? 'text-left' : ''}
          ${!buttonClassName.includes('bg-') ? 'bg-white disabled:bg-gray-100 hover:bg-gray-50' : ''}
          ${!buttonClassName.includes('border') ? 'border border-gray-300' : ''}
          ${!buttonClassName.includes('rounded-') ? 'rounded-md' : ''}
          ${!buttonClassName.includes('shadow-') ? 'shadow-sm' : ''}
          ${buttonClassName}
        `}
            >
                <div className="flex items-center overflow-hidden">
                    {!isMulti && options.find(opt => opt.value === value)?.indicatorColor && (
                        <span
                            className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                            style={{ backgroundColor: options.find(opt => opt.value === value).indicatorColor }}
                        />
                    )}
                    <span className={`block truncate ${((isMulti ? (Array.isArray(value) && value.length > 0 && !(value.length === 1 && value[0] === 'All')) : !!value)) ? ((!buttonClassName.match(/text-(gray|black|white|red|orange|amber|yellow|green|emerald|teal|cyan|blue|indigo|violet|purple|fuchsia|pink|rose)/) ? 'text-gray-900 ' : '') + (!buttonClassName.match(/font-/) ? 'font-medium' : '')) : 'text-gray-400'}`}>
                        {getDisplayText()}
                    </span>
                </div>
                <ChevronDownIcon
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Menu */}
            {isOpen && (() => {
                const menu = (
                    <div
                        ref={menuRef}
                        className={`portal-dropdown-content
            ${usePortal ? 'fixed' : 'absolute'} z-[9999] mt-1 ${usePortal ? '' : 'w-full'} bg-white border border-gray-200 rounded-lg shadow-xl
            ${maxMenuHeightClassName}
            overflow-hidden whitespace-normal
            ${menuClassName}
          `}
                        style={{
                            maxHeight: `${maxMenuHeightPx}px`,
                            visibility: (usePortal && !isPositioned) ? 'hidden' : 'visible',
                            opacity: (usePortal && !isPositioned) ? 0 : 1,
                            pointerEvents: (usePortal && !isPositioned) ? 'none' : 'auto',
                            ...(usePortal ? { top: menuStyle.top, left: menuStyle.left, minWidth: menuStyle.minWidth } : {})
                        }}
                    >
                        {/* Search Input */}
                        {searchable && (
                            <div className="p-2 border-b border-gray-100 sticky top-0 bg-white z-10">
                                <div className="relative">
                                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        ref={searchInputRef}
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        placeholder={searchPlaceholder}
                                        className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>
                        )}

                        {/* Options List */}
                        <div className="overflow-y-auto overflow-x-hidden py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-400" style={{ maxHeight: searchable ? `${maxMenuHeightPx - 50}px` : `${maxMenuHeightPx}px` }}>
                            {filteredOptions.length === 0 ? (
                                <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                    {searchTerm ? 'No results found' : 'No options available'}
                                </div>
                            ) : (
                                filteredOptions.map((option) => (
                                    <div key={option.value} className="relative group/opt">
                                        <button
                                            type="button"
                                            disabled={option.disabled}
                                            onMouseEnter={(e) => { setHoveredOption(option.value); setHoveredRect(e.currentTarget.getBoundingClientRect()); }}
                                            onMouseLeave={() => { setHoveredOption(null); setHoveredRect(null); }}
                                            onClick={() => !option.disabled && handleSelect(option.value)}
                                            className={`
                                                flex items-center w-full text-left px-3 py-2 transition-colors relative
                                                ${compact ? 'text-xs' : 'text-sm'}
                                                ${option.disabled
                                                    ? 'text-gray-400 italic cursor-not-allowed opacity-60 bg-gray-50'
                                                    : isSelected(option.value)
                                                        ? 'bg-indigo-50 text-indigo-700 font-semibold'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                }
                                            `}
                                        >
                                            {option.indicatorColor && (
                                                <span
                                                    className="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                                                    style={{ backgroundColor: option.indicatorColor }}
                                                />
                                            )}
                                            <span className="whitespace-nowrap flex-grow pr-2">{option.label}</span>
                                            {isSelected(option.value) && (
                                                <CheckCircleIcon className="h-4 w-4 text-indigo-600 ml-2 flex-shrink-0" />
                                            )}
                                        </button>

                                        {/* Tooltip for description — uses actual DOM rect for precise alignment */}
                                        {option.description && hoveredOption === option.value && hoveredRect && (
                                            <div className="fixed z-[10000] px-2 py-1.5 bg-gray-800 text-white text-[11px] rounded shadow-lg pointer-events-none max-w-xs whitespace-normal"
                                                style={{
                                                    left: hoveredRect.right + 8,
                                                    top: Math.max(8, Math.min(
                                                        window.innerHeight - 50,
                                                        hoveredRect.top + hoveredRect.height / 2 - 14
                                                    ))
                                                }}>
                                                {option.description}
                                                <div className="absolute left-0 top-1/2 transform -translate-x-full -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                );
                return usePortal ? ReactDOM.createPortal(menu, document.body) : menu;
            })()}
        </div>
    );
};

export default ScrollableDropdown;
